<template>
  <div class="agent-root">
    <!-- Floating action button -->
    <button
      v-if="!open"
      class="agent-fab"
      :title="`Ask ${cfg.name}`"
      @click="openChat"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
      </svg>
    </button>

    <!-- Chat panel -->
    <Transition name="agent-slide">
      <div v-if="open" class="agent-panel">

        <div class="agent-header">
          <div class="agent-header-left">
            <span class="agent-dot"></span>
            <div>
              <div class="agent-name">{{ cfg.name }}</div>
              <div class="agent-domain">{{ cfg.domain }}</div>
            </div>
          </div>
          <button class="agent-close" title="Close" @click="open = false">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <div class="agent-messages" ref="scrollEl">
          <div class="agent-welcome">{{ cfg.welcome }}</div>

          <template v-for="(msg, i) in messages" :key="i">
            <div class="agent-msg" :class="`agent-msg--${msg.role}`">
              <div class="agent-bubble">{{ msg.content }}</div>
            </div>
          </template>

          <div v-if="streamText || streaming" class="agent-msg agent-msg--assistant">
            <div class="agent-bubble">{{ streamText }}<span v-if="streaming" class="agent-cursor">▋</span></div>
          </div>

          <div v-if="errMsg" class="agent-error">{{ errMsg }}</div>
        </div>

        <form class="agent-compose" @submit.prevent="send">
          <input
            v-model="input"
            ref="inputEl"
            class="agent-input"
            :placeholder="streaming ? 'Responding…' : 'Ask a question…'"
            :disabled="streaming"
            autocomplete="off"
          />
          <button type="submit" class="agent-send-btn" :disabled="streaming || !input.trim()">
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
              <path d="M14.5 8L1.5 1.5l3 6.5-3 6.5L14.5 8z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
            </svg>
          </button>
        </form>

      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue';

const props = defineProps<{
  scope: 'ai' | 'consulting' | 'custom' | 'intern' | 'web' | 'main';
  agentUrl: string;
}>();

type Msg = { role: 'user' | 'assistant'; content: string };

const CONFIGS: Record<string, { name: string; domain: string; welcome: string }> = {
  ai:         { name: 'AI Assistant',          domain: 'ai.ridgecodetech.com',         welcome: 'Hi! Ask me anything about AI engineering services — LLM integration, RAG, workflow automation, or custom model training. What are you working on?' },
  consulting: { name: 'Consulting Assistant',  domain: 'consulting.ridgecodetech.com', welcome: 'Hi! I can help with questions about technical consulting — architecture reviews, technology decisions, or engineering strategy. What do you need help thinking through?' },
  custom:     { name: 'Dev Assistant',         domain: 'custom.ridgecodetech.com',     welcome: 'Hi! Ask me about custom software development — web apps, APIs, backend systems, or integrations. What are you building?' },
  intern:     { name: 'Intern Assistant',      domain: 'intern.ridgecodetech.com',     welcome: 'Hi! I can answer questions about the RidgeCodeTech internship program for students and universities. What would you like to know?' },
  web:        { name: 'Web Assistant',         domain: 'web.ridgecodetech.com',        welcome: 'Hi! I can help with questions about web development and managed hosting. What kind of site or project are you working on?' },
  main:       { name: 'RCT Assistant',         domain: 'ridgecodetech.com',            welcome: "Hi! I can help you find the right RidgeCodeTech service. What are you trying to build or accomplish?" },
};

const cfg = computed(() => CONFIGS[props.scope]);

const open      = ref(false);
const input     = ref('');
const messages  = ref<Msg[]>([]);
const streaming = ref(false);
const streamText = ref('');
const errMsg    = ref('');
const scrollEl  = ref<HTMLElement | null>(null);
const inputEl   = ref<HTMLInputElement | null>(null);

function openChat() {
  open.value = true;
  nextTick(() => inputEl.value?.focus());
}

async function scrollBottom() {
  await nextTick();
  if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight;
}

watch(streamText, scrollBottom);

async function send() {
  const text = input.value.trim();
  if (!text || streaming.value) return;

  errMsg.value   = '';
  input.value    = '';
  messages.value.push({ role: 'user', content: text });
  streaming.value = true;
  streamText.value = '';
  await scrollBottom();

  try {
    const res = await fetch(props.agentUrl, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ scope: props.scope, messages: messages.value }),
    });

    if (!res.ok || !res.body) {
      const data = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(data.error ?? `Request failed (${res.status})`);
    }

    const reader  = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (raw === '[DONE]') break;
        try {
          const evt = JSON.parse(raw) as { type: string; delta?: { type: string; text?: string } };
          if (evt.type === 'content_block_delta' && evt.delta?.type === 'text_delta' && evt.delta.text) {
            streamText.value += evt.delta.text;
          }
        } catch { /* skip malformed events */ }
      }
    }

    messages.value.push({ role: 'assistant', content: streamText.value });
  } catch (e) {
    errMsg.value = e instanceof Error ? e.message : 'Something went wrong. Please try again.';
  } finally {
    streaming.value  = false;
    streamText.value = '';
    await nextTick();
    inputEl.value?.focus();
  }
}
</script>

<style scoped>
.agent-root {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 9999;
  font-family: 'Inter', 'Space Grotesk', sans-serif;
}

/* ── FAB ── */
.agent-fab {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #091a38;
  border: 1px solid rgba(190,205,234,0.2);
  color: #e1ecff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(9,26,56,0.4);
  transition: background 0.2s, box-shadow 0.2s, transform 0.15s;
}
.agent-fab:hover {
  background: #0f264f;
  box-shadow: 0 6px 24px rgba(9,26,56,0.55);
  transform: translateY(-1px);
}

/* ── PANEL ── */
.agent-panel {
  width: 360px;
  height: 500px;
  background: #091a38;
  border: 1px solid rgba(190,205,234,0.15);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 48px rgba(9,26,56,0.6);
  overflow: hidden;
}

/* ── HEADER ── */
.agent-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid rgba(190,205,234,0.1);
  flex-shrink: 0;
}
.agent-header-left { display: flex; align-items: center; gap: 0.6rem; }
.agent-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #27ae60;
  flex-shrink: 0;
  box-shadow: 0 0 6px rgba(39,174,96,0.6);
}
.agent-name  { font-family: 'Space Grotesk', sans-serif; font-size: 0.8rem; font-weight: 600; color: #e1ecff; line-height: 1.2; }
.agent-domain { font-size: 0.6rem; color: #7a96c1; margin-top: 0.05rem; }
.agent-close {
  background: transparent;
  border: none;
  color: #7a96c1;
  cursor: pointer;
  padding: 0.3rem;
  display: flex;
  border-radius: 3px;
  transition: color 0.15s, background 0.15s;
}
.agent-close:hover { color: #e1ecff; background: rgba(255,255,255,0.06); }

/* ── MESSAGES ── */
.agent-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  scroll-behavior: smooth;
}
.agent-messages::-webkit-scrollbar { width: 3px; }
.agent-messages::-webkit-scrollbar-track { background: transparent; }
.agent-messages::-webkit-scrollbar-thumb { background: rgba(190,205,234,0.12); border-radius: 2px; }

.agent-welcome {
  font-size: 0.8rem;
  line-height: 1.65;
  color: #becdea;
  font-weight: 300;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(190,205,234,0.08);
  border-radius: 6px;
  padding: 0.85rem;
}

.agent-msg { display: flex; }
.agent-msg--user      { justify-content: flex-end; }
.agent-msg--assistant { justify-content: flex-start; }

.agent-bubble {
  max-width: 85%;
  padding: 0.6rem 0.85rem;
  border-radius: 6px;
  font-size: 0.8rem;
  line-height: 1.65;
  font-weight: 300;
  white-space: pre-wrap;
  word-break: break-word;
}
.agent-msg--user .agent-bubble {
  background: #0f264f;
  color: #e1ecff;
  border: 1px solid rgba(190,205,234,0.12);
}
.agent-msg--assistant .agent-bubble {
  background: rgba(255,255,255,0.04);
  color: #becdea;
  border: 1px solid rgba(190,205,234,0.08);
}

.agent-cursor {
  display: inline-block;
  animation: blink 0.8s step-end infinite;
  color: #7a96c1;
  margin-left: 1px;
}
@keyframes blink { 0%,100% { opacity:1 } 50% { opacity:0 } }

.agent-error {
  font-size: 0.76rem;
  color: #f5a0a0;
  background: rgba(180,60,60,0.1);
  border: 1px solid rgba(180,60,60,0.2);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
}

/* ── COMPOSE ── */
.agent-compose {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem;
  border-top: 1px solid rgba(190,205,234,0.1);
  flex-shrink: 0;
}
.agent-input {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 300;
  color: #e1ecff;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(190,205,234,0.12);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}
.agent-input:focus   { border-color: #7a96c1; background: rgba(255,255,255,0.08); }
.agent-input::placeholder { color: #3a5a7c; }
.agent-input:disabled { opacity: 0.5; cursor: not-allowed; }

.agent-send-btn {
  width: 34px;
  height: 34px;
  border-radius: 4px;
  background: #0f264f;
  border: 1px solid rgba(190,205,234,0.15);
  color: #becdea;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}
.agent-send-btn:hover:not(:disabled) { background: #1a3a6e; color: #e1ecff; }
.agent-send-btn:disabled { opacity: 0.35; cursor: not-allowed; }

/* ── TRANSITION ── */
.agent-slide-enter-active { transition: opacity 0.2s, transform 0.2s cubic-bezier(.4,0,.2,1); }
.agent-slide-leave-active { transition: opacity 0.15s, transform 0.15s; }
.agent-slide-enter-from  { opacity: 0; transform: translateY(10px) scale(0.97); }
.agent-slide-leave-to    { opacity: 0; transform: translateY(5px) scale(0.98); }

@media (max-width: 480px) {
  .agent-root  { bottom: 1rem; right: 1rem; }
  .agent-panel { width: calc(100vw - 2rem); height: 440px; }
}
</style>
